import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Typography, Button, Card, Spin, Result, Input, message, Alert, Space, Divider, Grid } from 'antd';
import { CheckCircleOutlined, EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Seo } from '../seo';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;
const API_BASE = process.env.REACT_APP_API_BASE || '';
const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';

export default function OrderConfirm() {
  const { orderCode } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [approving, setApproving] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders/confirm/${token}`);
      if (!res.ok) { setNotFound(true); return; }
      setState(await res.json());
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setApproving(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders/confirm/${token}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error();
      setState(await res.json());
      message.success('已確認，我們將盡快為您安排出貨');
    } catch { message.error('操作失敗，請稍後再試'); }
    finally { setApproving(false); }
  };

  const submitAdjust = async () => {
    if (!note.trim()) { message.warning('請描述想微調的地方'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders/confirm/${token}/adjust`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || '');
      if (d.needs_payment && d.checkout_url) {
        message.info('本次微調需先支付設計費，將前往付款…');
        window.location.href = d.checkout_url;
        return;
      }
      message.success('已收到您的微調需求，我們會盡快為您調整並再通知您確認');
      setAdjustOpen(false); setNote('');
      setState(d);
    } catch (e) { message.error(e.message || '送出失敗，請稍後再試'); }
    finally { setSubmitting(false); }
  };

  const container = { maxWidth: 820, margin: '0 auto', padding: isMobile ? '16px' : '24px 16px 48px' };

  if (loading) return <div style={{ ...container, textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
  if (notFound || !state) {
    return (
      <div style={container}>
        <Result status="404" title="連結無效或已失效" subTitle="此確認連結可能已過期，請聯繫我們或至會員中心查看訂單。"
          extra={<Link to="/orders"><Button type="primary">我的訂單</Button></Link>} />
      </div>
    );
  }

  const media = state.proof_media || [];
  const confirmed = state.confirmed;
  const awaitingPay = state.adjustment_status === 'awaiting_payment';

  return (
    <div style={container}>
      <Seo page="成品確認" description="確認您的專屬客製手鏈成品" path={`/orders/${orderCode}/confirm`} />
      <Link to="/orders" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: BRAND, marginBottom: 12 }}>
        <ArrowLeftOutlined /> 我的訂單
      </Link>
      <Title level={3} style={{ color: BRAND, fontWeight: 800, marginTop: 0 }}>成品確認</Title>
      <Text type="secondary">訂單編號 {state.order_code}</Text>

      {/* 成品照片 / 影片 */}
      <Card style={{ marginTop: 16, borderRadius: 16, borderColor: BORDER }} styles={{ body: { padding: isMobile ? 14 : 18 } }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>您的專屬手鏈成品 ✨</Text>
        {media.length === 0 ? (
          <Text type="secondary">尚無成品檔案</Text>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {media.map((m, i) => (
              <div key={i} style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#000' }}>
                {m.type === 'video'
                  ? <video src={m.url} controls playsInline style={{ width: '100%', display: 'block', maxHeight: 420 }} />
                  : <img src={m.url} alt={`成品 ${i + 1}`} style={{ width: '100%', display: 'block' }} />}
              </div>
            ))}
          </div>
        )}
      </Card>

      {confirmed ? (
        <Card style={{ marginTop: 16, borderRadius: 16, borderColor: BORDER, textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: 44, color: '#52c41a' }} />
          <Title level={4} style={{ marginTop: 12 }}>已確認{state.confirm_auto ? '（逾時自動確認）' : ''}</Title>
          <Paragraph type="secondary">感謝您的確認，我們將盡快為您安排出貨 📦</Paragraph>
        </Card>
      ) : awaitingPay ? (
        <Alert style={{ marginTop: 16 }} type="warning" showIcon
          message="微調設計費待付款"
          description="您先前的微調需求尚未完成付款；完成付款後我們才會進行微調。若要改為直接出貨，請點下方「確認無誤」。" />
      ) : null}

      {!confirmed && (
        <>
          <Alert style={{ marginTop: 16 }} type="info" showIcon
            message={`若超過 ${state.deadline_days} 天未回覆，我們將自動為您安排出貨`}
            description={state.deadline_at ? `預計自動出貨時間：${new Date(state.deadline_at).toLocaleDateString()}` : null} />

          <Card style={{ marginTop: 16, borderRadius: 16, borderColor: BORDER }}>
            {!adjustOpen ? (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Button type="primary" size="large" block icon={<CheckCircleOutlined />} loading={approving}
                  onClick={approve} style={{ height: 50, borderRadius: 12, background: BRAND, borderColor: BRAND, fontWeight: 700 }}>
                  確認無誤，通知出貨
                </Button>
                <Button size="large" block icon={<EditOutlined />} onClick={() => setAdjustOpen(true)}
                  style={{ height: 48, borderRadius: 12, borderColor: BRAND, color: BRAND }}>
                  想要微調
                </Button>
                <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
                  {(state.adjustment_count || 0) === 0
                    ? `第一次微調免費；之後每次加收設計費 NT$${state.design_fee}`
                    : `本次微調需加收設計費 NT$${state.design_fee}（付款後進行）`}
                </Text>
              </Space>
            ) : (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>想微調哪裡呢？請描述您的需求</Text>
                <TextArea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="例如：想把某顆珠子換個位置、扣頭想低調一點…" style={{ marginBottom: 8 }} />
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                  message={(state.next_adjust_fee || 0) === 0
                    ? '本次微調免費'
                    : `本次微調需先支付設計費 NT$${state.next_adjust_fee}，送出後將前往付款`} />
                <Space>
                  <Button type="primary" loading={submitting} onClick={submitAdjust}
                    style={{ background: BRAND, borderColor: BRAND }}>送出微調需求</Button>
                  <Button onClick={() => { setAdjustOpen(false); setNote(''); }}>取消</Button>
                </Space>
              </>
            )}
          </Card>
          <Divider />
          <Paragraph type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
            配色與選石屬命理設計的一部分，若需大幅更動建議重新客製；此處微調為外觀細節調整。
          </Paragraph>
        </>
      )}
    </div>
  );
}
